/**
 * @file    DiyDrawer.tsx
 * @scope   VIEW
 * @summary Drawer used to create/edit DIY widgets. Wires the domain hooks together and composes
 *          the header, the main tab, the Enterprise sub-views and the footer.
 * @consumes Global store (`useDrawerStore`), Guardrails API, engine plugins
 *           (./DiyWidgetEngine/plugins), ./common/*, ./DiyWidgetEngine/*
 * @produces Updates to `state.customWidget` and navigation between drawer tabs.
 * @sideEffects Delegated to the hooks below; this file owns no effect of its own.
 * @notes Phase 2 refactor:
 *        - the metadata effects, the chart-type-change effect and the draft-sync effect moved to
 *          `common/hooks` (one responsibility each);
 *        - the pure adapters moved to `common/hooks/useDrawerAdapters`;
 *        - the section prop groups moved to `Views/MainView/useMainViewProps`;
 *        - accordion/modal UI state now lives in the sections that own it;
 *        - the store selector is split per field and the context value is memoized, so an
 *          unrelated store change no longer re-renders the whole view tree.
 */
import React, { useState, useCallback, useMemo } from 'react';
import { SingleValue } from 'react-select';
import { useAxios } from 'hooks/useAxios';
import { DIYChartOption } from 'constants/chart-selector';
import './common/styles/DiyDrawer.scss';
import { useEngineInit } from './DiyWidgetEngine/hooks/useEngineInit';
import { useEngineValidation } from './DiyWidgetEngine/hooks/useEngineValidation';
import { useEnginePersistence } from './DiyWidgetEngine/hooks/useEnginePersistence';
import {
  DEFAULT_ENTERPRISE_CHART_TYPE,
  engineOptionHasTrend,
  resolveEngineOptionComponent,
} from './DiyWidgetEngine/chartTypes';
import {
  useEngineConfigChartTypeResolver,
  useEngineChartTypeResolver,
  useEngineGatedOptionPredicate,
} from './DiyWidgetEngine/chartTypes/useEngineChartTypeResolver';
import { useActiveEngineDescriptor } from './DiyWidgetEngine/hooks/useActiveEngineDescriptor';
import { useEngineSubtypeSwitch } from './DiyWidgetEngine/hooks/useEngineSubtypeSwitch';
import { restoreEngineTrend } from './DiyWidgetEngine/plugins/registry';
import EngineProviders from './DiyWidgetEngine/plugins/EngineProviders';
import EngineRoutes from './DiyWidgetEngine/plugins/EngineRoutes';
import {
  useEngineResetSelection,
  useEngineTabNavigation,
  useEngineTrendSync,
} from './DiyWidgetEngine/plugins/useEnginePlugins';
import DrawerHeader from './Views/DrawerHeader/DrawerHeader';
import DrawerFooter from './Views/DrawerFooter/DrawerFooter';
import MainView from './Views/MainView/MainView';
import { useMainViewProps } from './Views/MainView/useMainViewProps';
import { DrawerProvider } from './State/DrawerContext';
// --- DiyDrawer feature modules (see ./README.md for the folder map) -------------------------
import { DiyDrawerProps } from './common/types';
import { DiyDrawerMainTab } from './common/constants/tabs';
import { useWidgetOptions } from './common/hooks/useWidgetOptions';
import { useWidgetOptionSync } from './common/hooks/useWidgetOptionSync';
import { useDrawerLifecycle } from './common/hooks/useDrawerLifecycle';
import { useChartColors } from './common/hooks/useChartColors';
import { useQueryCollections } from './common/hooks/useQueryCollections';
import { useDrawerNavigation } from './common/hooks/useDrawerNavigation';
import { useChartTrend } from './common/hooks/useChartTrend';
import { useChartRules } from './common/hooks/useChartRules';
import { useQuestionsCalculations } from './common/hooks/useQuestionsCalculations';
import { useChartFilters } from './common/hooks/useChartFilters';
import { useCurrentAlias } from './common/hooks/useCurrentAlias';
import { useDrawerAdapters } from './common/hooks/useDrawerAdapters';
import { useChartMetadataFetch } from './common/hooks/useChartMetadataFetch';
import { useChartTypeChangeEffect } from './common/hooks/useChartTypeChangeEffect';
import { useWidgetDraftSync } from './common/hooks/useWidgetDraftSync';
import { useDrawerStore } from './common/hooks/useDrawerStore';

const DiyDrawerInner: React.FC<DiyDrawerProps> = ({ removeWidget, saveComponent }) => {
  const { response, axiosFetch, loading } = useAxios();
  const { response: responseData, axiosFetch: axiosFetchData, loading: loadingConfig } = useAxios();

  const {
    customWidget,
    widgets,
    widgetsList,
    endpoints,
    isConsumer,
    setState,
    selectedDiyChildChart,
    dataSourceType,
    dashboardView,
    workspace,
    useDiyHierarchyFilters,
    useAdvancedFilters,
    savedEnterpriseWidgetId,
  } = useDrawerStore();

  const [showDetail, setShowDetail] = useState<boolean>(true);
  const [ruleName, setRuleName] = useState<string>('');

  /**
   * LEGACY-ONLY measure selection: question/calculation option lists, the user's picks, their
   * mutual filtering and drag & drop ordering.
   */
  const {
    chartQuestions,
    chartCalculations,
    selectedQuestions,
    selectedCalculations,
    filteredQuestions,
    filteredCalculations,
    handleQuestionChange,
    handleCalculationChange,
    handleQuestionsDragEnd,
    handleCalculationsDragEnd,
    parseQuestionsAndCalculations,
    clearSelections,
    touchedQC,
    setTouchedQC,
  } = useQuestionsCalculations(customWidget);

  /**
   * LEGACY-ONLY rule engine: which Guardrails rules apply to the selected chart and whether the
   * current question/calculation combination satisfies any of them.
   */
  const {
    applicableRules,
    responseRules,
    setResponseRules,
    filterRules: applyRuleFilter,
    requiredQuestionsCalculations,
    requiredQuestions,
    requiredCalculations,
    selectRule,
    showQuestionCalculation,
  } = useChartRules();

  const [requiredDisplayName, setRequiredDisplayName] = useState<boolean>(false);
  const [textContentIsValid, setTextContentIsValid] = useState<boolean>(true);

  /**
   * Chart selection domain: the dropdown options, the selected one and the Enterprise chart type
   * derived from it. Single source of truth for the legacy/enterprise split.
   * WHY the injected registry: `common/` must not import from `Enterprise/` (isolation rule 2).
   * The resolver is flag-aware (§12): gated-off subtypes behave as legacy options.
   */
  const resolveEnterpriseChartType = useEngineChartTypeResolver();
  const resolveConfigChartType = useEngineConfigChartTypeResolver();
  const isOptionHidden = useEngineGatedOptionPredicate();
  const {
    chartTypes,
    setChartTypes,
    currentAlias,
    setCurrentAlias,
    enterpriseChartType,
    isDiyWidgetEngine,
    hasEnterpriseV2Config,
  } = useCurrentAlias({
    customWidgetConfig: customWidget.customWidgetConfig,
    setState,
    resolveEnterpriseChartType,
    defaultEnterpriseChartType: DEFAULT_ENTERPRISE_CHART_TYPE,
    resolveConfigChartType,
  });

  /**
   * Filtering domain (date ranges, hierarchy filters and advanced filters).
   * WHY: declared right after the Enterprise flag because the default date range must be skipped
   * for Enterprise widgets, and `buildCurrentSelection` needs the same flag.
   */
  const {
    chartFilters,
    selectedFilteringDateRange,
    hierarchyFiltersValues,
    advancedFiltersValues,
    parseChartFilters,
    applyDateRange,
    handleHierarchyFilterChange,
    handleAdvancedFilterChange,
    buildCurrentSelection,
  } = useChartFilters({ customWidget, isDiyWidgetEngine });

  const [decimalPlaces, setDecimalPlaces] = useState<any>(-1);

  const resetEngineSelection = useEngineResetSelection();

  // ENGINE: active subtype descriptor drives layout, validation and subtype switches (Phase 5).
  const activeEngineDescriptor = useActiveEngineDescriptor(enterpriseChartType?.id);
  useEngineSubtypeSwitch(activeEngineDescriptor);
  const engineLayout = activeEngineDescriptor?.hostLayout;

  const { isFieldsManagerSaveDisabled, isEnterpriseWidgetInvalid } =
    useEngineValidation(activeEngineDescriptor);
  const { applyEnterpriseDataToWidget } = useEnginePersistence(activeEngineDescriptor);

  /**
   * Trend domain (period options, selection, interval and store mirroring). The Enterprise restore
   * step is injected so the hook stays decoupled from the DiyEnterprise domain.
   */
  const {
    chartTrend,
    selectedChartTrend,
    setSelectedChartTrend,
    parseTrendOptions,
    clearTrendOptions,
    handleTrendChange,
    handleTrendCountChange,
    resetTrendToMinimum,
  } = useChartTrend({
    customWidget,
    setState,
    isDiyWidgetEngine,
    restoreTrend: restoreEngineTrend,
  });

  // Subtypes that keep their own trend (e.g. CPR) mirror the drawer control into their slice.
  useEngineTrendSync(activeEngineDescriptor, selectedChartTrend);

  const { currentTab, setCurrentTab, goToMainTab, selectedPartition, setSelectedPartition } =
    useDrawerNavigation();

  // Breadcrumb behaviour + per-subtype cleanup when sub-views are popped.
  const { handleNavigate, maxCrumbs } = useEngineTabNavigation({
    currentTab,
    setCurrentTab,
    selectedPartition,
  });

  /** Query/collections domain (Medusa query builder + data-collection catalogue). */
  const { availableCollections, setQueryData, queryIsValid, parseQueryCollections } =
    useQueryCollections(customWidget, setState);

  /**
   * Single owner of the edits mirrored into the widget draft (goal, Y axis, text block, title).
   * WHY here: it replaces four `useState` + four effects that used to live in this component, and
   * it is declared where those effects were so the surrounding effect order is unchanged.
   */
  const { payLoad, setGoalValues, setYAxisValues, setTextBlockData, setWidgetTitle } =
    useWidgetOptionSync({ customWidget, setState });

  // Change handlers backing the "Options"/"Actions" checkboxes. Declared after the sync hook
  // because it consumes its dispatchers; it registers no effect, so ordering is unaffected.
  const optionsHandlers = useWidgetOptions({
    customWidget,
    setState,
    setDecimalPlaces,
    setGoalValues,
    setYAxisValues,
  });

  // ENTERPRISE-ONLY: rehydrates the Enterprise builder from a saved config.
  useEngineInit(isDiyWidgetEngine, customWidget.customWidgetConfig);

  /**
   * Adapters injecting this component's context into the pure `common/utils`.
   * WHY declared here: the effect hooks below receive them as arguments, so they must already be
   * initialized when those hooks are called (not only when their effects run).
   */
  const {
    currentOptionsAlias,
    filterRules,
    setSourceType,
    defaultOptions,
    checkAlias,
    getDataSourceForPreview,
    getDataUrlEndpoint,
    getAlias,
    initializeChartTypes,
    applyChartConfig,
  } = useDrawerAdapters({
    customWidget,
    widgetsList,
    dataSourceType,
    dashboardView,
    endpoints,
    selectedDiyChildChart,
    currentAlias,
    selectedChartTrend,
    isDiyWidgetEngine,
    enterpriseChartType,
    setState,
    setRuleName,
    setCurrentAlias,
    setChartTypes,
    applyRuleFilter,
    setResponseRules,
    parseQuestionsAndCalculations,
    parseTrendOptions,
    clearTrendOptions,
    parseChartFilters,
    parseQueryCollections,
    isOptionHidden,
    resolveEngineComponent: resolveEngineOptionComponent,
    hasEngineTrend: engineOptionHasTrend,
  });

  // Guardrails metadata: chart-type catalog on mount + every chart Config that arrives.
  useChartMetadataFetch({
    endpoints,
    workspace,
    hasMedusaQuery: Boolean(customWidget.hasMedusaQuery),
    isConsumer,
    axiosFetch,
    response,
    responseData,
    onChartTypes: initializeChartTypes,
    onChartConfig: applyChartConfig,
  });

  /** Colour domain (pickers, buffers and store propagation). */
  const { setSelectedColor, setSelectedColorFamily, initColor, initColorFamily, colorByChartType } =
    useChartColors(customWidget, widgetsList, setState);

  // Rebuilds the widget draft whenever the user picks another chart type.
  useChartTypeChangeEffect({
    currentAlias,
    customWidget,
    chartTypes,
    widgetsList,
    selectedDiyChildChart,
    selectedPartition,
    responseRules,
    isDiyWidgetEngine,
    isConsumer,
    axiosFetchData,
    setState,
    setTouchedQC,
    clearSelections,
    resetTrendToMinimum,
    clearEnterpriseSelectedValues: resetEngineSelection,
    filterRules,
    hasAliasChanged: checkAlias,
    getDataSourceForPreview,
    getDataUrlEndpoint,
    buildOptions: defaultOptions,
    resolveSourceType: setSourceType,
    colorByChartType,
  });

  // Mirrors the current selection (questions, calculations, trend, filters…) into the draft.
  useWidgetDraftSync({
    currentAlias,
    customWidget,
    setState,
    isDiyWidgetEngine,
    selectedQuestions,
    selectedCalculations,
    selectedChartTrend,
    setSelectedChartTrend,
    chartTrend,
    decimalPlaces,
    hierarchyFiltersValues,
    advancedFiltersValues,
    selectedFilteringDateRange,
    selectedPartition,
    buildCurrentSelection,
    selectRule,
    currentOptionsAlias,
    getDataSourceForPreview,
    getAlias,
  });

  // Save / cancel / close lifecycle (event handlers only — no hook-ordering constraints).
  const {
    handleSaveClick,
    handleFieldsManagerSaveClick,
    fieldsManagerSaveDisabled,
    handleCloseClick,
    handleCancelClick,
    isDisabled,
  } = useDrawerLifecycle({
    customWidget,
    setState,
    widgets,
    payLoad,
    selectedChartTrend,
    isDiyWidgetEngine,
    applyEnterpriseDataToWidget,
    availableCollections,
    savedEnterpriseWidgetId,
    removeWidget,
    goToMainTab,
    saveComponent,
    selectedPartition,
    isFieldsManagerSaveDisabled,
    isEnterpriseWidgetInvalid,
    queryIsValid,
    textContentIsValid,
    requiredDisplayName,
    applicableRules,
    requiredQuestionsCalculations,
  });

  /** Selects another chart type and publishes it as the active child chart. */
  const handleChartTypeChange = useCallback(
    (newValue: SingleValue<DIYChartOption>) => {
      setState({ selectedDiyChildChart: newValue });
      if (newValue?.chartType) setCurrentAlias(newValue);
    },
    [setState, setCurrentAlias]
  );

  const toggleDetail = useCallback(() => setShowDetail((visible) => !visible), []);

  /**
   * WHY memoized: the value used to be a fresh object on every render, so every section reading
   * the context re-rendered even when none of these values had changed.
   */
  const drawerContextValue = useMemo(
    () => ({
      customWidget,
      setState,
      loading,
      currentAlias,
      isDiyWidgetEngine,
      enterpriseChartType,
      engineLayout,
      currentOptionsAlias,
    }),
    [
      customWidget,
      setState,
      loading,
      currentAlias,
      isDiyWidgetEngine,
      enterpriseChartType,
      engineLayout,
      currentOptionsAlias,
    ]
  );

  // Memoized prop groups for the main tab sections (see ./Views/MainView/useMainViewProps.ts).
  const mainViewProps = useMainViewProps({
    customWidget,
    loading,
    loadingConfig,
    hasCurrentAlias: Boolean(currentAlias),
    isDiyWidgetEngine,
    requiredDisplayName,
    setWidgetTitle,
    setRequiredDisplayName,
    chartTypes,
    onChartTypeChange: handleChartTypeChange,
    hasEnterpriseV2Config,
    chartTrend,
    selectedChartTrend,
    onTrendChange: handleTrendChange,
    onTrendCountChange: handleTrendCountChange,
    setSelectedChartTrend,
    applicableRules,
    chartQuestions,
    chartCalculations,
    filteredQuestions,
    filteredCalculations,
    selectedQuestions,
    selectedCalculations,
    onQuestionChange: handleQuestionChange,
    onCalculationChange: handleCalculationChange,
    onQuestionsDragEnd: handleQuestionsDragEnd,
    onCalculationsDragEnd: handleCalculationsDragEnd,
    showQuestionCalculation,
    requiredQuestions,
    requiredCalculations,
    requiredQuestionsCalculations,
    touchedQC,
    setTouchedQC,
    availableCollections,
    setSelectedPartition,
    setCurrentTab,
    setQueryData,
    initColor,
    initColorFamily,
    setSelectedColor,
    setSelectedColorFamily,
    setTextBlockData,
    setTextContentIsValid,
    optionsHandlers,
    ruleName,
    chartFilters,
    applyDateRange,
    selectedFilteringDateRange,
    useDiyHierarchyFilters,
    hierarchyFiltersValues,
    onHierarchyFiltersChange: handleHierarchyFilterChange,
    useAdvancedFilters,
    advancedFiltersValues,
    onAdvancedFiltersChange: handleAdvancedFilterChange,
  });

  return (
    <DrawerProvider value={drawerContextValue}>
      <div className={`diy-drawer ${showDetail ? 'open' : 'closed'}`}>
        <div className="diy-drawer-container">
          <DrawerHeader
            currentTab={currentTab}
            onNavigate={handleNavigate}
            maxCrumbs={maxCrumbs}
            showDetail={showDetail}
            onToggleDetail={toggleDetail}
          />

          {currentTab.name === DiyDrawerMainTab.name && <MainView {...mainViewProps} />}

          {/* Engine subtype sub-views (Enterprise fields, CPR programs, …). */}
          <EngineRoutes
            currentTab={currentTab}
            setCurrentTab={setCurrentTab}
            goToMainTab={goToMainTab}
            selectedPartition={selectedPartition}
            endpoint={getDataUrlEndpoint(currentAlias)}
            availableCollections={availableCollections}
          />

          <DrawerFooter
            currentTab={currentTab}
            onClose={handleCloseClick}
            onSave={handleSaveClick}
            saveDisabled={isDisabled(customWidget)}
            onFieldsManagerCancel={handleCancelClick}
            onFieldsManagerSave={handleFieldsManagerSaveClick}
            fieldsManagerSaveDisabled={fieldsManagerSaveDisabled()}
          />
        </div>
      </div>
    </DrawerProvider>
  );
};

/** Wraps the drawer with every registered engine subtype provider. */
const DiyDrawer: React.FC<DiyDrawerProps> = (props) => (
  <EngineProviders>
    <DiyDrawerInner {...props} />
  </EngineProviders>
);

export default DiyDrawer;
